import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import PoweredLogoWhiteVertical from 'src/assets/images/poweredby-white-vertical.svg'
import BackgroundShield from 'src/assets/images/background-shield.svg'
import { LoadingPage } from '../LoadingPage'
import { MobileBackArrowMajor } from '@shopify/polaris-icons'
import {
  getIsCollectDataFailed,
  getIsCollectDataFinished,
  getIsCollectDataStarted,
  getCollectDataStepsRemainingTimeToComplete,
} from 'src/selectors/collectData'
import { useNavigate } from 'react-router-dom'
import { OnboardingSuccessPage } from '../OnboardingSuccessPage'
import { addS, addSForString } from 'src/utils/baseUtils'
import { CLIENT_ROUTES } from 'src/constants/routes'
import { OnboardingFailedPage } from '../OnboardingFailedPage'
import { collectDataStepDataMap } from 'src/constants/collect-data/constants'
import { getCollectDataStepConfigs } from 'src/selectors/collectData'
import { StepConfigItem } from 'src/components/client/home/StepConfigItem'
import { Button } from 'src/components/shared/Button'
import { ClientHomePageHeader } from 'src/components/layout/client/ClientHomePageHeader'
import { useMediaQueries } from 'src/hooks/useMediaQueries'
import { GigSafeMemberBenefitPortal } from 'src/components/shared/GigSafeMemberBenefitPortal'
import { getPartner } from 'src/selectors/partner'
import { Footer } from 'src/components/shared/Footer'
import { getIsTokenSignIn } from 'src/selectors/auth'
import { DataCollectionSource } from 'src/constants/shared'

const ClientHomePageComponent = () => {
  const navigate = useNavigate()
  const { isDesktop, isMobile, isDesktopOrTablet } = useMediaQueries()

  const driver = useSelector(getDriver)
  const partner = useSelector(getPartner)
  const collectDataStepConfigs = useSelector(getCollectDataStepConfigs)
  const stepsTotalTimeToComplete = useSelector(getCollectDataStepsRemainingTimeToComplete)
  const isOnboardingStarted = useSelector(getIsCollectDataStarted)
  const isOnboardingFinished = useSelector(getIsCollectDataFinished)
  const isOnboardingFailed = useSelector(getIsCollectDataFailed)
  const isIsTokenSignIn = useSelector(getIsTokenSignIn)

  const firstDisabledStepConfigIndex = useMemo(
    () => collectDataStepConfigs.findIndex((item) => item.disabled),
    [collectDataStepConfigs],
  )

  const handleStartOnboarding = useCallback(() => {
    navigate(CLIENT_ROUTES.DOCS_URL)
  }, [])

  if (!driver) {
    return <LoadingPage flow="client" />
  }

  if (isOnboardingFailed) {
    return <OnboardingFailedPage />
  }

  if (isOnboardingFinished) {
    return (
      <OnboardingSuccessPage
        title={partner?.gigsafeConfig?.onboardingSuccessScreenTitle}
        description={partner?.gigsafeConfig?.onboardingSuccessScreenDescription}
      />
    )
  }

  return (
    <div>
      {!isIsTokenSignIn && <ClientHomePageHeader />}

      <div css={tw`h-[100vh] lg:overflow-y-scroll lg:flex`}>
        <div
          css={[
            tw`h-[100vh] bg-primary-800 pt-16 lg:float-left lg:w-[50%] flex flex-col items-center lg:pr-[14%] lg:items-end justify-end md:justify-end md:pb-[72px] overflow-hidden px-4 sm:px-0`,
            isIsTokenSignIn && tw`pt-0`,
          ]}
        >
          <img src={BackgroundShield} css={tw`absolute top-1/4 lg:w-[280px]`} />
          <div css={tw`lg:max-w-[296px] max-w-[390px] mx-4 flex flex-col`}>
            <Typography variant="overline" css={tw`mb-2 text-primary-200`}>
              {driver?.collectDataFlow === DataCollectionSource.Recollection
                ? 'RECOLLECTION'
                : 'ONBOARDING'}
            </Typography>
            <Typography variant="h1" css={tw`text-shades-0`}>
              Welcome,
            </Typography>
            <Typography css={tw`mb-2 text-shades-0`} variant="h1">
              {`${driver?.firstName} ${driver?.lastName}`}
            </Typography>
            <Typography css={tw`mb-6 text-primary-200`}>
              {`Tap "${isOnboardingStarted ? 'Continue' : 'Start'} ${
                driver?.collectDataFlow === DataCollectionSource.Recollection
                  ? 'Recollection'
                  : 'Onboarding'
              }" to complete essential steps and qualify for ${
                partner?.displayName ? addSForString(partner?.displayName) : "partner's"
              } job
              opportunities`}
            </Typography>
            <Button
              css={tw`bg-primary-100 hover:bg-primary-200 w-full`}
              onClick={handleStartOnboarding}
            >
              {`${isOnboardingStarted ? 'Continue' : 'Start'} ${
                driver?.collectDataFlow === DataCollectionSource.Recollection
                  ? ' Recollection'
                  : ' Onboarding'
              }`}
            </Button>
            {isMobile && (
              <>
                <Typography css={tw`mt-4 mb-2 text-center text-primary-200`}>
                  Continue scrolling to review the required steps
                </Typography>
                <MobileBackArrowMajor css={tw`-rotate-90 fill-shades-0`} height={20} />
                <div css={tw`w-[100%] h-[100px] bg-primary-700 [border-radius: 50%] mb-[-80px]`} />
              </>
            )}
            {isDesktop && (
              <div css={tw`mt-12 h-11 flex flex-1`}>
                <img src={PoweredLogoWhiteVertical} />
              </div>
            )}
          </div>
        </div>
        <div
          css={tw`lg:float-left lg:w-[50%] lg:overflow-y-auto bg-shades-50 lg:pl-[10%] lg:items-start lg:bg-primary-800 py-8 px-4 flex flex-col items-center`}
        >
          <div css={tw`max-w-[400px] lg:mt-32`}>
            <Typography css={isDesktop ? tw`text-shades-0` : undefined} variant="h2">
              Required steps
            </Typography>
            <Typography css={[tw`my-2`, isDesktop ? tw`text-primary-200` : tw`text-shades-500`]}>
              Progress is saved automatically after each step - pause at your convenience and resume
              later
            </Typography>
            <Typography
              variant="caption"
              css={isDesktop ? tw`text-shades-0` : tw`text-primary-500`}
            >
              {`Complete the process in approximately ${stepsTotalTimeToComplete} ${addS(
                stepsTotalTimeToComplete,
                'minute',
              )}.`}
            </Typography>
            <div css={tw`mb-4`} />
            {collectDataStepConfigs
              .filter((item) =>
                item.collectDataSteps.every((step) => Boolean(collectDataStepDataMap[step.type])),
              )
              .map((item, index) => (
                <StepConfigItem
                  key={item.id}
                  index={index + 1}
                  collectDataConfig={item}
                  disabled={firstDisabledStepConfigIndex === index}
                />
              ))}
            <div css={[tw`my-4`, !isDesktopOrTablet && tw`bg-info-200 p-2 rounded-3xl`]}>
              <GigSafeMemberBenefitPortal />
            </div>
            {!isDesktop && (
              <div css={tw`w-full`}>
                <Button
                  className={tw`w-full mt-6`}
                  variant="contained"
                  onClick={handleStartOnboarding}
                >
                  {`${isOnboardingStarted ? 'Continue' : 'Start'} Onboarding`}
                </Button>
                <Footer className={tw`flex items-center mt-6 justify-center w-full`} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ClientHomePage = memo(ClientHomePageComponent)
